import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fetchLeaderboard, type ModelPerformance } from "../api/client";
import { TOOLTIPS } from "../lib/tooltips";
import { isBackfilledModel } from "../lib/decisions";
import { HelpTooltip } from "../lib/HelpTooltip";
import { StatePill, STATE_FILTER_OPTIONS, matchesStateFilter } from "../lib/state";
import { AccountSummaryCard } from "./AccountSummaryCard";

type SortKey = keyof Pick<
  ModelPerformance,
  "rolling_sharpe_20" | "cum_return_pct" | "hit_rate" | "mean_return_per_pred" | "total_predictions"
>;

const COLS: { key: SortKey; label: string; tip: string; format: (v: number | undefined) => string }[] = [
  { key: "rolling_sharpe_20", label: "Sharpe (rolling)", tip: TOOLTIPS.sharpe, format: (v) => fmtNum(v, 3) },
  { key: "cum_return_pct", label: "Cumulative Return", tip: TOOLTIPS.cumulativeReturn, format: (v) => fmtPct(v) },
  { key: "hit_rate", label: "Hit rate", tip: TOOLTIPS.hitRate, format: (v) => fmtPct(v) },
  { key: "mean_return_per_pred", label: "Mean/pred", tip: TOOLTIPS.meanPerPred, format: (v) => fmtPct(v) },
  { key: "total_predictions", label: "N", tip: TOOLTIPS.nPredictions, format: (v) => String(v ?? 0) },
];

function fmtNum(v: number | undefined, digits = 2) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return Number(v).toFixed(digits);
}
function fmtPct(v: number | undefined) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return `${(Number(v) * 100).toFixed(2)}%`;
}
function pnlClass(v: number | undefined) {
  if (v === undefined) return "";
  return v > 0 ? "pos" : v < 0 ? "neg" : "";
}

export function LeaderboardPage() {
  const [tickerFilter, setTickerFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("rolling_sharpe_20");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const { data, isLoading, error } = useQuery({
    queryKey: ["leaderboard", tickerFilter],
    queryFn: () => fetchLeaderboard({ ticker: tickerFilter || undefined, limit: 200 }),
  });

  const sorted = useMemo(() => {
    const rows = (data?.models ?? []).filter((r) =>
      matchesStateFilter(stateFilter, r.state, Boolean(r.in_active_ensemble)),
    );
    return [...rows].sort((a, b) => {
      const av = Number(a[sortKey] ?? 0);
      const bv = Number(b[sortKey] ?? 0);
      return sortDir === "desc" ? bv - av : av - bv;
    });
  }, [data, stateFilter, sortKey, sortDir]);

  return (
    <div>
      <AccountSummaryCard mode="paper" />
      <div className="toolbar">
        <input
          placeholder="Filter by ticker (e.g. NVDA)"
          value={tickerFilter}
          onChange={(e) => setTickerFilter(e.target.value.toUpperCase())}
        />
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          style={{ marginLeft: 8, padding: "4px 8px", background: "#0b1220", color: "#e2e8f0", border: "1px solid #334155", borderRadius: 4 }}
        >
          {STATE_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value || "all"} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <span style={{ color: "#94a3b8", fontSize: 13, marginLeft: 8 }}>
          {data ? `${sorted.length} of ${data.count} models` : isLoading ? "loading…" : ""}
        </span>
      </div>

      {error && <div className="card" style={{ color: "#fca5a5" }}>{String(error)}</div>}

      <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Model</th>
            <th>Ticker</th>
            <th>State</th>
            {COLS.map((c) => (
              <th
                key={c.key}
                className="num"
                onClick={() => {
                  if (c.key === sortKey) setSortDir(sortDir === "desc" ? "asc" : "desc");
                  else { setSortKey(c.key); setSortDir("desc"); }
                }}
              >
                <HelpTooltip text={c.tip}>
                  {c.label} {sortKey === c.key ? (sortDir === "desc" ? "↓" : "↑") : ""}
                </HelpTooltip>
              </th>
            ))}
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.model_id}>
              <td>
                <Link to={`/models/${encodeURIComponent(row.model_id)}`}>
                  <code>{row.model_id}</code>
                </Link>
                {isBackfilledModel(row.model_id) && (
                  <HelpTooltip
                    text="Synthetic model_id from scripts/backfill_observability.py. Reconstructed from DecisionLog; per-model attribution is not available because individual_action was not logged at decision time."
                  >
                    <span
                      style={{
                        marginLeft: 8,
                        padding: "1px 6px",
                        borderRadius: 3,
                        background: "#1f2937",
                        color: "#fbbf24",
                        fontSize: 10,
                        fontWeight: 600,
                        verticalAlign: "middle",
                      }}
                    >
                      BACKFILL
                    </span>
                  </HelpTooltip>
                )}
              </td>
              <td>
                {row.ticker ? (
                  <Link to={`/symbols/${encodeURIComponent(row.ticker)}`}>{row.ticker}</Link>
                ) : "—"}
              </td>
              <td>
                <StatePill state={row.state} active={Boolean(row.in_active_ensemble)} />
              </td>
              {COLS.map((c) => {
                const v = row[c.key] as number | undefined;
                const cls = c.key === "cum_return_pct" || c.key === "mean_return_per_pred" ? pnlClass(v) : "";
                return (
                  <td key={c.key} className={`num ${cls}`}>{c.format(v)}</td>
                );
              })}
              <td style={{ color: "#94a3b8", fontSize: 12 }}>{row.last_updated ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {!isLoading && sorted.length === 0 && (
        <div style={{ padding: 24, color: "#94a3b8", textAlign: "center" }}>
          No models yet — predictions accumulate as the decision engine runs.
        </div>
      )}
    </div>
  );
}
